import { Popover, Tooltip } from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import BlockIcon from "@material-ui/icons/Block";
import CommentIcon from "@material-ui/icons/Comment";
import CreateIcon from "@material-ui/icons/Create";
import DescriptionIcon from "@material-ui/icons/Description";
import InfoIcon from "@material-ui/icons/Info";
import BookingIcon from "@material-ui/icons/Unarchive";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import SharedDocsTab from "../../../Common_Component/SharedDocsTab";
import { ApplicationDataAPI } from "../API/ApplicationDataAPI";
import useAPIError from "../APIError";
import { shouldRenderAmendments } from "./Amendments/amendmentUtil";
import BookingDialog from "./BookingsInfo/BookingDialog";
import FullScreenDialog from "./FullScreenDialog";
import Notes from "./Notes/index";

import RestorePageIcon from "@material-ui/icons/RestorePage";
import ResubmitRequest from "./Resubmit/index";

const useStyles = makeStyles(() => ({
	menuButton: {
		color: "white",
	},
	rfi: {
		color: "white",
		backgroundColor: "rgb(46, 59, 85)",
		border: "1px solid white",
		height: "30px",
		width: "30px",
		fontSize: "15px",
	},
	docsPopoverPaper: {
		width: "60%",
		maxHeight: "calc(100%)",
		overflow: "auto",
		top: "65px !important",
	},
}));

export const getAppType = () => {
	const title = document.title;
	if (title.includes("DEAL REVIEW") || title.includes("DEAL DnA REVIEW")) {
		return "Verbal";
	}
	if (title.includes("DOCUMENT DATA REVIEW")) return "Document_Data_Review";
	if (title.includes("PRE-DELIVERY")) return "Document_Check_In";
	if (title.includes("DOCUMENTS SENT")) return "Document_Sent";
	if (
		title.includes("FUNDING REVIEW") ||
		title.includes("FUNDING CONFIRMATION") ||
		title.includes("DOCUMENT REVIEW")
	)
		return "Funding";
	return undefined;
};

export default function TopBarIcons({ app, isReadOnly }) {
	const baseURL = process.env.REACT_APP_BASE_API_URL;
	const classes = useStyles();
	const { addError } = useAPIError();
	const [menuItems, setMenuItems] = useState([]);
	const [rfiList, setRfiList] = useState({ pending: [], closed: [] });
	const [dialogMode, setDialogMode] = useState("hide");
	const [dialogTitle, setDialogTitle] = useState("");
	const [dialogName, setDialogName] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [docsEl, setDocsEl] = useState(null);
	const [resubmitEl, setResubmitEl] = useState(null);
	const [openBooking, setOpenBooking] = useState(false);
	const [canCloseNotes, setCanCloseNotes] = useState(true);
	const [openNotesDialog, setOpenNotesDialog] = useState(false);
	const docsShellRef = useRef(null);

	const doesAmendmentsRender = useMemo(() => {
		return shouldRenderAmendments(app?.status);
	}, [app?.status]);

	useEffect(() => {
		(async () => {
			if (app?.id) {
				let menus = [];
				if (doesAmendmentsRender) {
					menus.push({
						name: "amendments",
						title: "Amendments",
						icon: (
							<Tooltip title="Amendments">
								<CreateIcon />
							</Tooltip>
						),
					});
				}
				menus = [
					...menus,
					{
						name: "docs",
						title: "Documents",
						icon: (
							<Tooltip title="Documents">
								<DescriptionIcon />
							</Tooltip>
						),
					},
					{
						name: "app_info",
						title: "Application Information",
						icon: (
							<Tooltip title="App Info">
								<InfoIcon />
							</Tooltip>
						),
					},
					{
						name: "notes",
						title: "Notes",
						icon: (
							<Tooltip title="Notes">
								<CommentIcon />
							</Tooltip>
						),
					},
					{
						name: "rfi",
						title: "Application RFIs",
						icon: (
							<Badge badgeContent={rfiList?.pending?.length} color="primary">
								<Avatar className={classes.rfi}>RFI</Avatar>
							</Badge>
						),
					},
					{
						name: "cancel",
						title: "Cancel Application",
						icon: (
							<Tooltip title="Cancel">
								<BlockIcon />
							</Tooltip>
						),
					},
				];

				setMenuItems(menus);
			}
		})();
	}, [app?.id, rfiList?.pending?.length, doesAmendmentsRender]);

	useEffect(() => {
		(async () => {
			if (app?.id) {
				loadRfis();
			}
		})();
	}, [app?.id]);

	const loadRfis = async () => {
		try {
			const rfisResponse = await ApplicationDataAPI.fetchApplicationRfisById(
				app.id,
				"ALL",
				app.locked_by_email
			);
			const rfis = rfisResponse.sort(function (a, b) {
				return new Date(b.request_date) - new Date(a.request_date);
			});
			const pendingRfi = [];
			const closedRfi = [];
			rfis.forEach((rfi) => {
				if (rfi.rfi_status === "Closed") closedRfi.push(rfi);
				else pendingRfi.push(rfi);
			});
			setRfiList({
				pending: pendingRfi,
				closed: closedRfi,
			});
		} catch (err) {
			addError(err?.message);
		}
	};

	const handleMenuItemClick = (event, name, title) => {
		if (
			name !== "logout" &&
			name !== "notes" &&
			name !== "booking" &&
			name !== "docs" &&
			name !== "resubmit"
		) {
			setDialogName(name);
			setDialogTitle(title);
			setDialogMode("show");
		}
		if (name === "notes") {
			setAnchorEl(event.currentTarget);
		}
		if (name === "docs") {
			setDocsEl(event.currentTarget);
		}
		if (name === "booking") {
			setOpenBooking((pre) => !pre);
		}
		if (name === "resubmit") {
			setResubmitEl(event.currentTarget);
		}
	};

	const handleDialogClose = (event) => {
		setDialogMode("hide");
	};

	const handleClose = () => {
		if (canCloseNotes) setAnchorEl(null);
		else setOpenNotesDialog(true);
	};

	const handleDocsClose = useCallback(() => {
		if (docsShellRef.current?.unmount) docsShellRef.current.unmount();
		setDocsEl(null);
	}, []);

	return (
		<div>
			{app.id !== undefined &&
				app.id !== "" &&
				(document.title.includes("DEAL REVIEW") ||
					document.title.includes("DOCUMENT DATA REVIEW") ||
					document.title.includes("FUNDING REVIEW") ||
					document.title.includes("FUNDING CONFIRMATION") ||
					document.title.includes("DOCUMENTS SENT") ||
					document.title.includes("DOCUMENT REVIEW") ||
					document.title.includes("FUNDING REVIEW") ||
					document.title.includes("PRE-DELIVERY")) &&
				process.env.REACT_APP_RESUBMIT != "hide" && (
					<IconButton
						edge="start"
						className={classes.menuButton}
						color="inherit"
						aria-label="Resubmit"
						title={"New Resubmit Request"}
						onClick={(event) =>
							handleMenuItemClick(event, "resubmit", "New Resubmit Request")
						}>
						<RestorePageIcon />
					</IconButton>
				)}
			{app.id !== undefined &&
				app.id !== "" &&
				document.title.includes("FUNDING CONFIRMATION") && (
					<IconButton
						edge="start"
						className={classes.menuButton}
						color="inherit"
						aria-label="Booking"
						title={"Book to Aspire"}
						onClick={(event) => handleMenuItemClick(event, "booking", "Booking")}>
						<BookingIcon />
					</IconButton>
				)}
			{app.id !== undefined && app.id !== "" && app?.is_franchise && (
				<IconButton
					edge="start"
					className={classes.menuButton}
					color="inherit"
					aria-label="franchise_info"
					title={"Franchise Deal Info"}
					onClick={(event) =>
						handleMenuItemClick(event, "franchise_info", "Franchise Deal Info")
					}>
					<AccountBalanceIcon />
				</IconButton>
			)}

			{app.id
				? menuItems.map((item, index) => (
						<IconButton
							edge="start"
							key={index}
							className={classes.menuButton}
							color="inherit"
							aria-label={item.name}
							onClick={(event) => handleMenuItemClick(event, item.name, item.title)}>
							{item.icon}
						</IconButton>
				  ))
				: ""}
			{/* Full-screen dialogs; docs panel uses SharedDocsTab (REACT_APP_SHARED_DOCS_TAB_URL), appType from getAppType(). */}
			<span id="dialog-root">
				<FullScreenDialog
					mode={dialogMode}
					title={dialogTitle}
					name={dialogName}
					app={app}
					callback={handleDialogClose}
					rfiList={rfiList}
					loadRfis={loadRfis}
					fullScreen={dialogName !== "cancel" ? true : false}
					isReadOnly={isReadOnly}
					appType={getAppType()}
				/>
			</span>
			{/* Setting the anchor element for popover, not working if passing as props in above dialog */}
			{anchorEl && <Notes onClose={handleClose} anchorEl={anchorEl} app={app} />}
			{/* Docs MFE: Popover with SharedDocsTab. Shell loads REACT_APP_SHARED_DOCS_TAB_URL and shows spinner until ready. Close: unmount then clear docsEl. */}
			{docsEl && (
				<Popover
					open={Boolean(docsEl)}
					anchorEl={docsEl}
					onClose={handleDocsClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					transformOrigin={{ vertical: "top", horizontal: "center" }}
					classes={{ paper: classes.docsPopoverPaper }}
					disableEnforceFocus
					disableAutoFocus
					disableRestoreFocus>
					<SharedDocsTab
						ref={docsShellRef}
						onClose={handleDocsClose}
						anchorEl={docsEl}
						app={app}
						isReadOnly={false}
						setCanCloseNotes={setCanCloseNotes}
						appType={getAppType()}
					/>
				</Popover>
			)}
			{resubmitEl && (
				<ResubmitRequest
					resubmitEl={resubmitEl}
					anchorEl={resubmitEl}
					setResubmitEl={setResubmitEl}
					app={app}
				/>
			)}
			{/*
			{resubmitEl && (
				<NewResubmitRequest
					anchorEl={resubmitEl}
					setResubmitEl={setResubmitEl}
					app={app}
					isReadOnly={isReadOnly}
				/>
            )}*/}
			<BookingDialog open={openBooking} setOpen={setOpenBooking} app={app} />
			<Dialog
				open={openNotesDialog}
				onClose={() => setOpenNotesDialog(false)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description">
				<DialogTitle id="alert-dialog-title">{"Unsaved Notes!"}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Your changes will be lost.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpenNotesDialog(false);
						}}>
						Cancel
					</Button>
					<Button
						onClick={() => {
							setOpenNotesDialog(false);
							setCanCloseNotes(true);
							setAnchorEl(null);
						}}
						autoFocus>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
